import { Component, inject, Injector, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { ResponsiveHideConditionsService } from '@app/finxone-web-frontend/app/lib/services/responsive-hide-conditions-service/responsive-hide-conditions.service';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { RESOLVED_FORM_GROUP_PATH } from '@app/finxone-web-frontend/app/lib/utils/di-tokens';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
  GenericHandleBarModel,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { getWidgetClasses } from '@app/finxone-web-frontend/app/widget.utis';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  AccountTypes,
  DatePickerTypes,
  DropdownMenuDirection,
  FormFieldConfig,
  InputFormatType,
  NavigationOptionsType,
  ProviderClass,
  WidgetNames,
  baseCssConfigStyle,
  commonContentStyleList,
  commonTextContentStyleList,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Subject, takeUntil } from 'rxjs';
import { ZonePageUrlProvider } from '../../zones/zone-url-provider';
import { defaultValidationMessages } from './default-validation-messages';
import {
  AngularValidationType,
  ValidationConfig,
  ValidationConfigType,
} from '@finxone-platform/ui-components';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-input-field-widget',
  templateUrl: 'input-field-widget.component.html',
})
export class InputFieldWidgetComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public InputMode = InputFormatType;
  public formGroup: FormGroup;
  public validationConfig: ValidationConfig[] = [];
  public dropdownOptions: any[] = [];

  public readonly DatePickerTypes = DatePickerTypes;
  public defaultVisibleItemsCount = 5;
  public defaultMenuDirection = DropdownMenuDirection.AUTO;

  public verifyButtonGlobalStylingClass = '';
  public isVerifyButtonGlobalStylingAllowed = false;
  public verifyButtonStyle: baseCssConfigStyle;
  public verifyButtonTextContent: string;
  public options: { value: string | number; label: string }[];
  public renderedHelperTextContent: string;
  public defaultAccountType = AccountTypes.CURRENT_ACCOUNT;
  nestedFormId: string;
  public currencyAmountFieldTextStyleClass: string;
  shouldHide: boolean;
  public hideVerificationButton = false;
  public disableAutocomplete = false;
  public renderedLabelTooltipTextContent: string;
  public renderedSliderMinValue: number;
  public renderedSliderMaxValue: number;

  private baseHandlebarModel: GenericHandleBarModel;
  private readonly resolvedFormGroupPath = inject(RESOLVED_FORM_GROUP_PATH, { optional: true });

  constructor(
    private dataIn: ProviderClass<any>,
    public configService: ConfigService,
    private workflowFormsService: WorkflowFormsService,
    private store: Store,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    public injector: Injector,
    private zonePageUrlProvider: ZonePageUrlProvider,
    private responsiveHideConditionsService: ResponsiveHideConditionsService,
  ) {}

  ngOnInit() {
    if (this.dataIn.widgetConfig.attributes?.['usingResponsiveHideCondition']) {
      this.setupResponsiveHideConditionSubscription();
    }

    // Should disable auto complete on the form field in the browser
    this.disableAutocomplete =
      this.dataIn.widgetConfig.attributes.widgetProperties?.['disableAutocomplete'] ?? false;

    if (this.inputFieldConfig) {
      this.setupHandlebarModelSubscriptionAndRenderTemplates();
      this.options =
        this.dataIn.widgetConfig.attributes.widgetProperties?.['inputFieldConfig']?.options ?? [];
      if (this.resolvedFormGroupPath) {
        // Provided by a 'group'/'array' typed Form Widget ancestor - use its resolved nested
        // path directly rather than recomputing a plain page-scoped one from parentForm below.
        this.nestedFormId = this.resolvedFormGroupPath;
      } else {
        const parentForm =
          this.dataIn.widgetConfig.attributes.widgetProperties?.['inputFieldConfig']?.parentForm;
        if (!parentForm)
          console.error(
            'parentForm ID is missing from input widget! ',
            this.dataIn.widgetConfig.attributes.widgetProperties?.['inputFieldConfig']?.id,
          );
        this.nestedFormId = this.workflowFormsService.generateCustomFormId(
          parentForm ?? '',
          this.zonePageUrlProvider,
        );
      }
      this.currencyAmountFieldTextStyleClass =
        this.dataIn.widgetConfig.attributes.widgetProperties?.['inputFieldConfig']?.textStyleClass ?? 'h6';
      const form = this.workflowFormsService.getFormPart(this.nestedFormId);

      let newFormGroup = new FormGroup({});

      if (this.inputFieldConfig?.dateFormat === DatePickerTypes.INPUT) {
        newFormGroup = new FormGroup({
          [this.inputFieldConfig?.day || 'day']: new FormControl(this.inputFieldConfig?.defaultValue ?? ''),
          [this.inputFieldConfig?.month || 'month']: new FormControl(
            this.inputFieldConfig?.defaultValue ?? '',
          ),
          [this.inputFieldConfig?.year || 'year']: new FormControl(this.inputFieldConfig?.defaultValue ?? ''),
        });
      }
      // add country code form control when inputFormat is PHONE_NUMBER and attachToInput is false
      else if (this.inputFieldConfig?.inputFormat === InputFormatType.PHONE_NUMBER) {
        if (!this.inputFieldConfig?.phoneNumberConfig?.attachToInput) {
          newFormGroup = new FormGroup({
            [this.inputFieldConfig?.countryCode || 'countryCode']: new FormControl('+44'),
          });
        }
      } else {
        newFormGroup = new FormGroup({
          [this.inputFieldConfig.name]: new FormControl(this.inputFieldConfig?.defaultValue ?? ''),
        });
      }

      this.addDummyControlToFormForSliderInput(form, newFormGroup);
      this.formGroup = form ?? newFormGroup;

      this.validationConfig = this.getValidationConfig(this.inputFieldConfig);
      this.getNestedWidgetGlobalStyling();
    }
  }
  /**
   * Adds a dummy control to the form for slider input which is later used to determine whether
   * the value provided by slider input is percentage or absolute number type value
   * @param form - The form group to add the control to
   * @param customFormGroup - The custom form group to add the control to
   */
  private addDummyControlToFormForSliderInput(form: FormGroup | undefined, customFormGroup: FormGroup) {
    if (this.inputFieldConfig?.inputFormat !== InputFormatType.SLIDER) {
      return;
    }
    if (!form) {
      customFormGroup.addControl(
        'sliderInputValueType',
        new FormControl(this.inputFieldConfig?.sliderValueType),
      );
      return;
    }
    form.addControl('sliderInputValueType', new FormControl(this.inputFieldConfig?.sliderValueType));
  }

  /**
   * Sets up a subscription to the widget's responsive hide condition and toggles visibility.
   *
   * If the condition is true, resets the associated FormControl and hides the widget's wrapper
   * by adding the 'd-none' class to the element identified via getWidgetClasses(...).
   * If false, removes 'd-none' to show the widget.
   *
   * The subscription is lifecycle-managed using takeUntil(this.destroy$).
   *
   * @private
   * @returns void
   * @see getWidgetClasses
   */
  private setupResponsiveHideConditionSubscription(): void {
    this.responsiveHideConditionsService
      .setupHideCondition(this.dataIn.widgetConfig.attributes['responsiveHideCondition'])
      .pipe(takeUntil(this.destroy$))
      .subscribe((shouldHide) => {
        // Date-picker fields (dateFormat === INPUT) decompose into separate day/month/year
        // controls rather than a single control named after the field - all of them need their
        // required validator toggled here, not just inputFieldConfig.name, or a hidden required
        // date field left empty permanently fails page validation even though it's invisible.
        const controlNames = [
          this.inputFieldConfig?.name,
          this.inputFieldConfig?.day,
          this.inputFieldConfig?.month,
          this.inputFieldConfig?.year,
        ].filter((name): name is string => !!name);

        if (shouldHide) {
          controlNames.forEach((name) => {
            const formControl = this.formGroup?.controls[name];
            // reset the form control, as this.formGroup has the reference of original formGroup.
            // this reset would reflect on that as well.
            formControl?.reset();
            // remove required validator if it is present when hiding the input
            if (this.inputFieldConfig?.validation?.required) {
              //setting time out to remove required validator
              setTimeout(() => {
                this.formGroup?.controls[name]?.removeValidators(Validators.required);
                this.formGroup?.controls[name]?.updateValueAndValidity();
              });
            }
          });
          // find the parent wrapper element for this widget present in main widget and hide it
          const widgetParentEl = document.getElementsByClassName(
            getWidgetClasses(this.dataIn.widgetConfig),
          )[0];
          widgetParentEl?.classList.add('d-none');
        } else {
          // find the parent wrapper element for this widget present in main widget and show it
          const widgetParentEl = document.getElementsByClassName(
            getWidgetClasses(this.dataIn.widgetConfig),
          )[0];
          widgetParentEl?.classList.remove('d-none');
          // add required validator if it is present when showing the input
          if (this.inputFieldConfig?.validation?.required) {
            controlNames.forEach((name) => {
              const formControl = this.formGroup?.controls[name];
              formControl?.addValidators(Validators.required);
              formControl?.updateValueAndValidity();
            });
          }
        }
      });
  }

  private setupHandlebarModelSubscriptionAndRenderTemplates() {
    buildHandleBarModel(this.store, '', this.countriesCurrenciesService, this.iconCacheService)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.baseHandlebarModel = model;
        this.renderHelperText();
        this.renderSliderMinMaxHandlebar();
      });
  }

  private renderHelperText() {
    const template = Handlebars.compile(this.inputFieldConfig?.inputHelperText ?? '');
    const inputLabelTooltipTextTemplate = Handlebars.compile(
      this.inputFieldConfig?.inputLabelTooltipText ?? '',
    );
    if (this.inputFieldConfig?.inputHelperText)
      this.renderedHelperTextContent = filterIfHandlebarNotRendered(template(this.baseHandlebarModel));
    if (this.inputFieldConfig?.inputLabelTooltipText)
      this.renderedLabelTooltipTextContent = filterIfHandlebarNotRendered(
        inputLabelTooltipTextTemplate(this.baseHandlebarModel),
      );
  }

  private getNestedWidgetGlobalStyling() {
    const nestedButtonWidgetConfig =
      this.dataIn.widgetConfig.nestedWidgets?.[WidgetNames.VERIFY_INPUT_FIELD_BUTTON];

    if (nestedButtonWidgetConfig) {
      if (
        nestedButtonWidgetConfig.widgetProperties?.['globalStylingClass'] &&
        nestedButtonWidgetConfig.widgetProperties?.['globalStyling']
      ) {
        this.verifyButtonGlobalStylingClass =
          nestedButtonWidgetConfig.widgetProperties?.['globalStylingClass'] ?? '';
        this.isVerifyButtonGlobalStylingAllowed =
          nestedButtonWidgetConfig.widgetProperties?.['globalStyling'] ?? false;
      }

      this.verifyButtonStyle = this.configService.getSpecificBaseCssStyle(nestedButtonWidgetConfig, [
        ...commonContentStyleList,
        ...commonTextContentStyleList,
      ]);
      this.verifyButtonTextContent = nestedButtonWidgetConfig.widgetProperties?.textContent;
    }
  }

  private renderSliderMinMaxHandlebar() {
    if (this.inputFieldConfig?.inputFormat !== InputFormatType.SLIDER) {
      return;
    }
    if (this.inputFieldConfig?.minValue) {
      const template = Handlebars.compile(this.inputFieldConfig.minValue);
      this.renderedSliderMinValue = Number(filterIfHandlebarNotRendered(template(this.baseHandlebarModel)));
    }
    if (this.inputFieldConfig?.maxValue) {
      const template = Handlebars.compile(this.inputFieldConfig.maxValue);
      this.renderedSliderMaxValue = Number(filterIfHandlebarNotRendered(template(this.baseHandlebarModel)));
    }
  }

  public get inputFieldConfig(): FormFieldConfig | undefined {
    return this.dataIn.widgetConfig.attributes.widgetProperties?.['inputFieldConfig'];
  }

  public getFormControlByName(formControlName: string) {
    return (this.formGroup.get(formControlName) as FormControl) || new FormControl();
  }

  private getValidationConfig(config: FormFieldConfig): ValidationConfig[] {
    const validation = config?.validation;

    if (!validation) {
      return [];
    }

    const getMessage = (key: ValidationConfigType, fallback: string) =>
      config.validationMessages?.[key] ?? fallback;

    const validationRules: Array<
      ValidationConfig & {
        enabled?: boolean | number;
      }
    > = [
      {
        enabled: validation.required,
        type: AngularValidationType.Required,
        message: getMessage(ValidationConfigType.Required, defaultValidationMessages.required(config.label)),
      },
      {
        enabled: validation.requiredTrue,
        type: AngularValidationType.RequiredTrue,
        message: getMessage(ValidationConfigType.RequiredTrue, defaultValidationMessages.requiredTrue),
      },
      {
        enabled: validation.email,
        type: AngularValidationType.Email,
        message: getMessage(ValidationConfigType.Email, defaultValidationMessages.email),
      },
      {
        enabled: validation.minLength,
        type: AngularValidationType.MinLength,
        message: getMessage(
          ValidationConfigType.MinLength,
          defaultValidationMessages.minlength(validation.minLength ?? 0),
        ),
        params: validation.minLength,
      },
      {
        enabled: validation.maxLength,
        type: AngularValidationType.MaxLength,
        message: getMessage(
          ValidationConfigType.MaxLength,
          defaultValidationMessages.maxlength(validation.maxLength ?? 0),
        ),
        params: validation.maxLength,
      },
      {
        enabled: validation.phoneNumber,
        type: AngularValidationType.PhoneValidation,
        message: getMessage(ValidationConfigType.PhoneNumber, defaultValidationMessages.phoneValidation),
        params: {
          attachToInput: this.inputFieldConfig?.phoneNumberConfig?.attachToInput,
          countryCodeKey: this.inputFieldConfig?.countryCode,
        },
      },
    ];

    return validationRules
      .filter(({ enabled }) => !!enabled)
      .map((rule) => ({
        type: rule.type,
        message: rule.message,
        ...(rule.params !== undefined ? { params: rule.params } : {}),
        ...(rule.validatorFn !== undefined ? { validatorFn: rule.validatorFn } : {}),
      }));
  }

  public get widgetNavigationOptions() {
    const navigationOptions: NavigationOptionsType = {
      navigationOption: this.dataIn?.widgetConfig?.attributes['navigationOption'],
      zone: this.dataIn?.widgetConfig?.attributes?.['zone'],
      pageUrl: this.dataIn?.widgetConfig?.attributes?.['pageUrl'],
      role: this.dataIn?.widgetConfig?.attributes?.['role'],
      pageId: this.dataIn?.widgetConfig?.attributes?.['pageId'],
      tabRef: this.dataIn?.widgetConfig?.attributes?.['tabRef'] ?? '',
      pageTransition: this.dataIn?.widgetConfig?.attributes?.['pageTransition'] ?? 'forward',
    };
    return navigationOptions;
  }
}
